import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, X, User as UserIcon } from "lucide-react";

/**
 * Circular avatar that, when hovered, exposes an upload + clear control.
 * - `url`: current avatar URL (joined with BACKEND).
 * - `initials`: shown on the silhouette placeholder when no image.
 * - `onUpload(file)`: async callback receiving the chosen file.
 * - `onClear()`: optional async callback to remove the avatar.
 * - `size`: pixel size of the circle (default 56).
 * - `testid`: data-testid prefix.
 */
export default function AvatarUpload({
  url, initials = "?", onUpload, onClear, size = 56, testid = "avatar",
}) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  // Resolve `url` into an absolute URL for the <img> tag.
  // Accepts: null/"" → no avatar, an absolute http(s) URL, or a backend-relative
  // path like "/api/static/avatars/..." which needs to be prefixed with BACKEND.
  let absUrl = null;
  if (url) {
    absUrl = url.startsWith("http") ? url : `${BACKEND}${url}`;
  }

  const pick = () => fileRef.current?.click();

  const onChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image must be 5 MB or smaller");
      return;
    }
    setBusy(true);
    try {
      await onUpload(f);
      toast.success("Avatar updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clear = async () => {
    if (!onClear) return;
    setBusy(true);
    try {
      await onClear();
      toast.success("Avatar removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative group flex-shrink-0" style={{ width: size, height: size }}
      data-testid={`${testid}-wrap`}>
      <input ref={fileRef} type="file" accept="image/*" className="hidden"
        onChange={onChange} data-testid={`${testid}-input`} />
      <button
        type="button" onClick={pick} disabled={busy}
        className="relative w-full h-full rounded-full overflow-hidden border border-zinc-200 bg-zinc-100 flex items-center justify-center"
        title="Upload avatar"
        data-testid={`${testid}-pick`}>
        {absUrl ? (
          <img src={absUrl} alt={initials}
            className="w-full h-full object-cover"
            data-testid={`${testid}-img`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-400">
            <UserIcon style={{ width: size * 0.55, height: size * 0.55 }} strokeWidth={1.5} />
          </div>
        )}
        {/* Hover camera overlay */}
        <span className="absolute inset-0 bg-black/45 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
          <Camera className="h-4 w-4" />
        </span>
      </button>
      {absUrl && onClear && (
        <button type="button" onClick={clear} disabled={busy}
          className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-zinc-900 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
          title="Remove avatar"
          data-testid={`${testid}-clear`}>
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
